import { useEffect, useRef, useState } from 'react';

// Inline SVG sparkline + a buffer hook that accumulates scalar readings over
// time. Extracted verbatim from fleet-dashboard.tsx so every page renders the
// same minimal trend line without pulling in a charting dependency.

const SPARKLINE_MAX_POINTS = 12;

export function Sparkline({
  points,
  color = '#6366f1',
}: {
  points: number[];
  color?: string;
}): JSX.Element {
  const W = 56;
  const H = 18;

  if (points.length < 2) {
    return <svg width={W} height={H} />;
  }

  const max = Math.max(...points, 1);
  const xs = points.map((_, i) => (i / (points.length - 1)) * W);
  const ys = points.map((v) => H - (v / max) * (H - 2) - 1);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden>
      <polyline
        points={xs.map((x, i) => `${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity="0.8"
      />
      {/* Latest point dot */}
      <circle cx={xs[xs.length - 1].toFixed(1)} cy={ys[ys.length - 1].toFixed(1)} r="2" fill={color} />
    </svg>
  );
}

/** Accumulates up to N readings of a scalar value; returns the buffer. */
export function useSparklineBuffer(
  value: number | undefined,
  maxPoints = SPARKLINE_MAX_POINTS,
): number[] {
  const [buffer, setBuffer] = useState<number[]>([]);
  const prev = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (value === undefined) return;
    if (value === prev.current) return;
    prev.current = value;
    setBuffer((b) => [...b.slice(-(maxPoints - 1)), value]);
  }, [value, maxPoints]);

  return buffer;
}
